// Cliente de Chatbularis IA contra nuestra API REST.
//
// El paquete original hablaba con Cloud Functions vía httpsCallable y guardaba
// el historial en Firestore con onSnapshot. Acá no hay Firebase: todo pasa por
// el mismo helper `api()` que usa el resto del panel, con el JWT del estudio, y
// el historial vive en Postgres aislado por tenant.
import { api, ApiError, TENANT, API_URL, getToken } from './api';
import { bytesDe } from './zip';
import type { Fuente } from './importarCarpetas';

/* ── Razonamiento: con cuál modo responde ────────────────────────────
   Tres modos, nombrados por la tarea y no por la velocidad:

   - Redactar (Sonnet 5): el trabajo diario —escritos completos, revisiones,
     jurisprudencia—. Es el por defecto: en svabogadas tres de cada cuatro
     consultas ya se hacían en Sonnet, redacta bien y cuesta unas cuatro veces
     menos que Opus.
   - Conversar (Sonnet 5, esfuerzo medio): dudas puntuales, respuesta breve.
   - Análisis profundo (Opus 5): solo para lo que exige pensar más. Se elige a
     propósito, con su costo a la vista.

   Los identificadores no cambiaron (rapido, conversacion, profundo): son los
   que acepta el servidor (ver RAZONAMIENTOS en backend/src/lib/asistente.ts) y
   los que quedaron guardados en las conversaciones. Los nombres de modelo de
   acá son solo para mostrar: el modelo de verdad lo decide el servidor. */
export type Razonamiento = 'profundo' | 'rapido' | 'conversacion';

export const RAZONAMIENTOS: {
  id: Razonamiento;
  etiqueta: string;
  modelo: string;
  para: string;
  detalle: string;
}[] = [
  {
    id: 'rapido',
    etiqueta: 'Redactar',
    modelo: 'Sonnet 5',
    para: 'Escritos completos y revisiones',
    detalle:
      'Redacta demandas y escritos completos, revisa contratos y escritos, busca jurisprudencia y cita la ley vigente. Es el modo para el trabajo de todos los días.',
  },
  {
    id: 'conversacion',
    etiqueta: 'Conversar',
    modelo: 'Sonnet 5 · ágil',
    para: 'Preguntas y dudas, como a un colega',
    detalle:
      'Para conversar: qué plazo aplica, si algo procede, cómo se calcula. Busca y cita las mismas fuentes que los otros modos, piensa menos y responde breve.',
  },
  {
    id: 'profundo',
    etiqueta: 'Análisis profundo',
    modelo: 'Opus 5',
    para: 'Casos difíciles',
    detalle:
      'Para lo que exige pensar más: la estrategia de un caso complejo, expedientes largos con contradicciones, recursos de nulidad o casación. Demora más y cuesta unas cuatro veces lo que Redactar.',
  },
];

/** Si no se elige nada, Redactar: hace bien el trabajo diario por una fracción
 *  de lo que cuesta el análisis profundo. */
export const RAZONAMIENTO_POR_DEFECTO: Razonamiento = 'rapido';

/** La última elección se recuerda por estudio: quien pasa la mañana revisando
 *  demandas no tiene por qué volver a elegir en cada consulta. Va junto al
 *  token del redactor, con la misma separación por estudio. */
// "-v2": cuando Redactar pasó a ser el modo por defecto, la elección guardada
// se reinició. Si no, quien había dejado Profundo seguiría en Opus sin saberlo.
const CLAVE_RAZONAMIENTO = `redactor_razonamiento_v2_${TENANT || 'sin-estudio'}`;

export const razonamientoGuardado = (): Razonamiento => {
  const v = localStorage.getItem(CLAVE_RAZONAMIENTO);
  return RAZONAMIENTOS.some(r => r.id === v) ? (v as Razonamiento) : RAZONAMIENTO_POR_DEFECTO;
};

export const guardarRazonamiento = (r: Razonamiento) => localStorage.setItem(CLAVE_RAZONAMIENTO, r);

export type MensajeIA = {
  rol: 'user' | 'assistant';
  texto: string;
  fuentes?: string[];
  /** Con cuál se generó la respuesta. Las de antes de que se pudiera elegir no
   *  lo traen, y se muestran sin marca: inventarles una sería mentir. */
  razonamiento?: Razonamiento;
  /** Los documentos que el asistente no pudo leer para esta respuesta, y por
   *  qué. Van al pie de la respuesta. */
  noLeidos?: NoLeido[];
  /** Lo que se le descontó al estudio por esta respuesta, en pesos. Las de
   *  antes del prepago no lo traen. */
  cobradoClp?: number;
  /** El texto seleccionado en Word con que se hizo la pregunta. */
  seleccion?: string;
  /** La pregunta llevó el documento de Word completo, de este largo (en
   *  caracteres). El texto no se guarda. */
  documentoWord?: number;
  ts: string;
};

export type NoLeido = { nombre: string; motivo: string };

/** Un archivo adjunto a la consulta, ya leído por el servidor. `texto` no
 *  viaja al navegador: son cientos de miles de caracteres que acá no se usan. */
export type DocumentoAdjunto = {
  id: string;
  nombre: string;
  tipo: string | null;
  /** texto | imagen | escaneo — cómo se obtuvo el contenido. Las dos últimas
   *  son transcripciones y el panel las marca como tales. */
  origen: string;
  caracteres: number;
  /** El documento del caso del que salió, si se adjuntó desde el caso. */
  documentoId?: string | null;
  /** leyendo | leido | parcial | error. Un expediente escaneado largo se lee
   *  en segundo plano; mientras, el adjunto está "leyendo". */
  estado?: 'leyendo' | 'leido' | 'parcial' | 'error';
  /** Por qué quedó parcial o en error. */
  motivo?: string | null;
  paginas?: number | null;
  /** Se reutilizó una lectura anterior del mismo documento del caso. */
  reutilizado?: boolean;
  creado: string;
};

/** El caso del panel al que está vinculada una conversación: lo justo para
 *  reconocerlo en la barra lateral y en la cabecera del chat. */
export type CasoVinculado = { id: string; codigo: string; cliente: string };

export type ConversacionResumen = {
  id: string;
  titulo: string;
  /** Solo id y nombre: la lista del historial no baja el resto. */
  documentos: { id: string; nombre: string }[];
  caso: CasoVinculado | null;
  creado: string;
  actualizado: string;
};

export type Conversacion = Omit<ConversacionResumen, 'documentos'> & {
  mensajes: MensajeIA[];
  documentos: DocumentoAdjunto[];
  /** Hay una consulta de esta conversación corriendo en el servidor: la
   *  respuesta todavía no está en `mensajes`, pero va a llegar. */
  enCurso?: boolean;
};

/* ── Límites de los adjuntos ───────────────────────────────────────────
   Están duplicados del servidor a propósito: acá sirven para avisar ANTES de
   subir treinta megas por una conexión lenta y recibir el rechazo al final.
   El que manda es el del servidor (routes/asistente.ts y lib/ocr.ts); si se
   cambia allá, hay que cambiarlo acá. */

/** Tope de un archivo subido desde el computador. Viaja en el cuerpo de la
 *  petición; los documentos del caso se leen desde el servidor y admiten más. */
export const MAX_ARCHIVO_MB = 25;
/** Las imágenes ya no tienen un tope aparte: el servidor reduce las grandes
 *  antes de leerlas (antes, cualquier foto de celular de más de 5 MB se
 *  rechazaba). */
export const MAX_IMAGEN_MB = MAX_ARCHIVO_MB;
/** Cuántos archivos admite una misma consulta. No es un límite de trabajo:
 *  un caso puede tener decenas de documentos y todos se leen. Es un tope de
 *  cordura. Espejo de MAX_DOCS en backend/src/lib/asistente.ts. */
export const MAX_DOCS = 300;

/** Lo que ofrece el diálogo de archivos del navegador. El servidor reconoce
 *  el formato por el contenido, así que esto solo filtra la vista. */
export const ACCEPT_ADJUNTOS =
  '.pdf,.doc,.docx,.odt,.rtf,.txt,.md,.csv,.xls,.xlsx,.ods,.ppt,.pptx,.odp,.eml,.html,.htm,' +
  '.jpg,.jpeg,.png,.webp,.gif,.heic,.heif,.tif,.tiff,.bmp,.avif,' +
  'application/pdf,application/msword,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,' +
  'text/plain,text/csv,message/rfc822,image/*';

const esImagen = (f: File) => f.type.startsWith('image/') || /\.(jpe?g|png|webp|gif|heic|heif|tiff?|bmp|avif)$/i.test(f.name);

/** Revisa un archivo antes de subirlo. Devuelve el motivo del rechazo, o null
 *  si está bien. */
export function revisarArchivo(file: File): string | null {
  if (!file.size) return `"${file.name}" está vacío.`;
  const limite = esImagen(file) ? MAX_IMAGEN_MB : MAX_ARCHIVO_MB;
  if (file.size > limite * 1024 * 1024) {
    return `"${file.name}" pesa ${(file.size / 1024 / 1024).toFixed(1)} MB y el máximo ${esImagen(file) ? 'para imágenes ' : ''}es ${limite} MB.`;
  }
  return null;
}

/** Saldo prepago de Chatbularis del estudio. Cada consulta lo descuenta; si no
 *  alcanza para empezar, el servidor responde 429 con el motivo. Viaja en cada
 *  respuesta para poder avisar antes de que se acabe. */
export type SaldoIA = {
  /** Lo que le queda, en pesos. Puede ser negativo: la consulta que empezó con
   *  saldo termina aunque se pase. */
  saldoClp: number;
  /** El estudio no se detiene y se le descuenta al costo: el estudio del
   *  propio dueño y la cortesía. El pie lo muestra como gasto. */
  sinTope: boolean;
  /** No alcanza para empezar una consulta en ningún modo. */
  agotado: boolean;
  /** Todavía alcanza, pero conviene cargar. */
  porAgotarse: boolean;
  /** En qué modos puede empezar una consulta con lo que le queda. */
  puede: Record<Razonamiento, boolean>;
};

export type RespuestaConsulta = {
  conversacionId: string;
  respuesta: string;
  fuentes: string[];
  razonamiento: Razonamiento;
  tokens: { input: number; output: number; cache_read: number; cache_write: number };
  /** El saldo ya descontada esta consulta. */
  saldo?: SaldoIA;
  /** Lo que costó esta consulta, en pesos. */
  cobradoClp?: number | null;
  /** El caso vinculado a la conversación después de esta consulta. */
  caso?: CasoVinculado | null;
  /** El caso vinculado ya no se puede ver (papelera, o dejó de ser tuyo): la
   *  respuesta salió sin su ficha. */
  casoPerdido?: boolean;
  /** Los documentos que no pudo leer para esta respuesta, y por qué. */
  noLeidos?: NoLeido[];
};

/* ── Casos del panel ───────────────────────────────────────────────────
   Una conversación puede trabajar sobre un caso del estudio. El servidor
   reconoce en la pregunta un código, un rol, un RUT o un nombre, y ANTES de
   responder pide confirmar cuál es (409 confirmar_caso). Nada se vincula sin
   ese "sí". */

/** Un caso tal como aparece para confirmar o elegir. */
export type CasoCandidato = {
  id: string;
  codigo: string;
  codigoPublico: string | null;
  cliente: string;
  tema: string | null;
  estado: string | null;
  rut: string | null;
  rolCaso: string | null;
  abogada: string | null;
  contraparte: string | null;
  parteRepresentada: string | null;
  /** Cuántos documentos tiene guardados el caso. */
  documentos?: number;
  /** Por qué lo propone el servidor: publico | rol | rut | nombre. */
  motivo?: string;
  /** "código 2026-0007", "rol O-123-2026", "nombre Pérez"… */
  detalle?: string;
};

/** La ficha para confirmar, con los documentos del caso que se pueden adjuntar. */
export type CasoFicha = {
  id: string;
  codigo: string;
  codigoPublico: string | null;
  cliente: string;
  rut: string;
  tema: string | null;
  estado: string | null;
  rolCaso: string;
  contraparte: string;
  parteRepresentada: string;
  abogado: string;
  creado: string;
  documentos: { id: string; nombre: string; tipo: string | null; size: number | null; creado: string }[];
};

/** La ficha como tarjeta: es lo que pintan los diálogos. Una sola
 *  conversión, para que un campo nuevo no aparezca en un diálogo y falte en
 *  el otro. */
export const candidatoDeFicha = (f: CasoFicha): CasoCandidato => ({
  id: f.id,
  codigo: f.codigo,
  codigoPublico: f.codigoPublico,
  cliente: f.cliente,
  tema: f.tema,
  estado: f.estado,
  rut: f.rut || null,
  rolCaso: f.rolCaso || null,
  abogada: f.abogado || null,
  contraparte: f.contraparte || null,
  parteRepresentada: f.parteRepresentada || null,
  documentos: f.documentos.length,
});

/** Lo que devuelve el servidor cuando hay que confirmar el caso antes de
 *  responder. */
export type ConfirmacionCaso = {
  candidatos: CasoCandidato[];
  vinculado: CasoVinculado | null;
  mensaje: string;
  /** Qué se va a redactar, cuando se llega desde la ficha del caso ("Demanda
   *  de alimentos"). Se muestra al confirmar y deja escrita la primera consulta. */
  tarea?: string;
};

/** ¿El error pide confirmar un caso? Devuelve los candidatos si es así. */
export function pideConfirmarCaso(e: unknown): ConfirmacionCaso | null {
  const err = e as ApiError;
  if (!(err instanceof ApiError) || err.status !== 409 || err.datos?.motivo !== 'confirmar_caso') return null;
  return { candidatos: err.datos.candidatos ?? [], vinculado: err.datos.vinculado ?? null, mensaje: err.message };
}

export const buscarCasos = (q: string) => api<CasoCandidato[]>(`/asistente/casos?q=${encodeURIComponent(q)}`);

export const fichaCaso = (id: string) => api<CasoFicha>(`/asistente/casos/${id}`);

/** Vincular la conversación a un caso, o soltarlo con null. Devuelve los
 *  adjuntos que quedan: al cambiar de caso, el servidor quita los que vinieron
 *  del caso anterior. */
export const vincularCaso = (conversacionId: string, casoId: string | null) =>
  api<{ id: string; caso: CasoVinculado | null; documentos: DocumentoAdjunto[] }>(`/asistente/conversaciones/${conversacionId}/caso`, { method: 'PATCH', body: { casoId } });

/** Adjuntar a la consulta un documento que ya está en el caso vinculado. El
 *  servidor lo baja de S3 y lo lee; si es un escaneo lo transcribe (y eso
 *  descuenta de la cuota, como cualquier adjunto). */
export const adjuntarDocumentoDelCaso = (conversacionId: string, documentoId: string) =>
  api<DocumentoAdjunto>(`/asistente/conversaciones/${conversacionId}/documentos/desde-caso`, {
    method: 'POST',
    body: { documentoId },
    timeoutMs: 5 * 60 * 1000,
  });

/* ── Biblioteca del estudio ────────────────────────────────────────────
   Los modelos y escritos propios del estudio, con los que Chatbularis redacta
   a SU manera. Es material del estudio y solo del estudio: aislado por
   estudio en la base y en S3, y nunca pasa a la base jurídica compartida.
   No entrena ningún modelo: en cada consulta se le pasan al asistente los
   trozos que se parecen a lo que se pregunta, como referencia de forma. */

export type DocumentoBiblioteca = {
  id: string;
  nombre: string;
  tipo: string | null;
  origen: string;
  caracteres: number;
  size: number | null;
  /** procesando | listo | error */
  estado: string;
  error: string | null;
  fragmentos: number;
  subidoPor: string | null;
  creado: string;
  indexado: string | null;
};

/* ── Formato de los archivos ───────────────────────────────────────────
   ESPEJO de FUENTES_ESCRITO, TAMANOS_ESCRITO, INTERLINEADOS_ESCRITO,
   MARGENES_ESCRITO y FORMATO_ESCRITO_DEFAULT en backend/src/lib/ajustes.ts.
   El servidor valida contra sus listas y reemplaza en silencio lo que no
   esté en ellas, así que si se agrega una letra allá hay que agregarla acá,
   y al revés. */
export const FUENTES_ESCRITO = ['Times New Roman', 'Arial', 'Calibri', 'Cambria', 'Garamond', 'Verdana'] as const;
export const TAMANOS_ESCRITO = [10, 11, 12, 13, 14] as const;
export const INTERLINEADOS_ESCRITO = [1, 1.15, 1.5, 2] as const;
export const MARGENES_ESCRITO = [2, 2.5, 3] as const;

/** Cómo se arman el Word y el PDF del estudio. */
export type FormatoEstudio = {
  fuente: (typeof FUENTES_ESCRITO)[number];
  tamano: (typeof TAMANOS_ESCRITO)[number];
  interlineado: (typeof INTERLINEADOS_ESCRITO)[number];
  margenCm: (typeof MARGENES_ESCRITO)[number];
};

/** Times 12 a espacio y medio con 2,5 cm: lo del foro chileno. Se usa solo
 *  mientras el formato del estudio no ha llegado del servidor. */
export const FORMATO_ESCRITO_DEFAULT: FormatoEstudio = { fuente: 'Times New Roman', tamano: 12, interlineado: 1.5, margenCm: 2.5 };

export type Biblioteca = {
  documentos: DocumentoBiblioteca[];
  /** Las instrucciones de estilo del estudio. */
  estilo: string;
  /** Letra, tamaño, interlineado y márgenes de los archivos que se descargan. */
  formato: FormatoEstudio;
  maxDocumentos: number;
  maxEstilo: number;
  /** Tope de texto por documento; lo que sobra se recorta al subir. */
  maxCaracteres: number;
  /** Cuánto puede durar una indexación. Pasado eso, la que sigue en
   *  "procesando" se quedó a medias y se ofrece reintentar. */
  indexacionMaxMs: number;
  /** Quien mira puede subir y borrar modelos (administra o es abogado del estudio). */
  puedeEditar: boolean;
  /** Quien mira puede cambiar el estilo (administra). */
  puedeEditarEstilo: boolean;
};

export const listarBiblioteca = () => api<Biblioteca>('/asistente/biblioteca');

export async function subirABiblioteca(file: File) {
  const contenidoBase64 = await aBase64(file);
  return api<DocumentoBiblioteca & { recortado?: boolean }>('/asistente/biblioteca', {
    method: 'POST',
    body: { nombre: file.name, contenidoBase64, mimeType: file.type },
    timeoutMs: 5 * 60 * 1000,
  });
}

export const reindexarBiblioteca = (id: string) => api(`/asistente/biblioteca/${id}/indexar`, { method: 'POST' });

export const eliminarDeBiblioteca = (id: string) => api(`/asistente/biblioteca/${id}`, { method: 'DELETE' });

/** El estilo se guarda como cualquier otro ajuste del estudio. */
export const guardarEstilo = (estilo: string) =>
  api<{ chatbularis: { estilo: string; formato: FormatoEstudio } }>('/ajustes', { method: 'PATCH', body: { chatbularis: { estilo } } });

export const guardarFormato = (formato: FormatoEstudio) =>
  api<{ chatbularis: { estilo: string; formato: FormatoEstudio } }>('/ajustes', { method: 'PATCH', body: { chatbularis: { formato } } });

/** Prefijo con que el servidor marca las fuentes que salieron de la
 *  biblioteca del estudio (ver PREFIJO_FUENTE en lib/biblioteca.ts). */
export const PREFIJO_FUENTE_ESTUDIO = 'Estudio · ';

/** Un movimiento de la cuota: la cuota del mes, la prueba, un consumo o un ajuste. */
export type MovimientoIA = {
  id: string;
  /** bienvenida | carga | consumo | ajuste (y cuota | prueba, de antes del prepago) */
  tipo: string;
  /** Positivo carga, negativo consume. */
  montoClp: number;
  /** El saldo que quedó después. */
  saldoClp: number;
  /** asistente | ocr en los consumos. */
  origen: string | null;
  detalle: string | null;
  ts: string;
};

/** Lo que suele costar una consulta en un modo, en pesos. */
export type PrecioModo = { desdeClp: number; tipicoClp: number; hastaClp: number };

/** Una opción de carga: lo que se paga y el saldo de regalo que trae. */
export type OpcionCarga = { montoClp: number; bonoClp: number };

/** Saldo del estudio y en qué se le fue. Van juntos porque la pregunta nunca es
 *  solo "¿cuánto me queda?" sino "¿cuánto me queda y en qué se me fue?". */
export type EstadoSaldo = SaldoIA & {
  /** Lo que costó el mes, con las devoluciones ya descontadas. */
  gastadoMesClp: number;
  /** Lo devuelto en el mes, si hubo algo. */
  devueltoMesClp: number;
  consultasMes: number;
  movimientos: MovimientoIA[];
  /** El gasto de cada mes (hora de Chile), del más reciente al más antiguo. */
  historial: { desde: string; gastadoClp: number; devueltoClp: number; consultas: number; lecturas: number; lecturasClp: number }[];
  /** Lo que suele costar una consulta en cada modo. */
  precios: Record<Razonamiento, PrecioModo>;
  /** Saldo mínimo para empezar una consulta en cada modo. */
  minimos: Record<Razonamiento, number>;
  /** Cuánto se puede cargar. */
  cargas: OpcionCarga[];
  /** Quien mira puede cargar (administra el estudio y el pago en línea está activo). */
  puedeCargar: boolean;
  /** El pago en línea está activo en el servidor. */
  cargaEnLinea: boolean;
};

/** El estudio avisa que pagó su suscripción.
 *
 *  NO renueva nada: manda un correo al dueño para que mire la cuenta y
 *  registre el pago real, que es lo que renueva y confirma. El micrositio del
 *  banco no confirma nada de vuelta, así que sin esa revisión el pago lo
 *  escribiría quien paga. */
export const avisarPago = (montoClp: number, medio: 'banchile' | 'transferencia', referencia?: string) =>
  api<{ ok: true; contacto: string }>('/ajustes/pago/aviso', { method: 'POST', body: { montoClp, medio, referencia } });

export const estadoSaldo = () => api<EstadoSaldo>('/asistente/saldo');

/** Lo que costaría el panel de gestión para este estudio, para proponérselo
 *  al que solo tiene Chatbularis. Es el mismo estado del plan que ve
 *  Configuración; solo administración. */
export type PlanEstudio = {
  conPanel: boolean; cupos: number; abogadosActivos: number; ciclo: string; periodoFin: string | null;
  /** precios[i] = mensualidad del panel con i+1 abogados. */
  precios: (number | null)[];
  cobro: { totalClp: number; panelClp: number; meses: number } | null;
};
export const planDelEstudio = () => api<PlanEstudio>('/ajustes/plan');

/** Sumar el panel de gestión al estudio que solo tiene Chatbularis. Rige al
 *  instante, con la prueba de 14 días del panel. */
export const contratarPanel = () => api<PlanEstudio>('/ajustes/panel', { method: 'PATCH' });

// ── Equipo del estudio que solo tiene Chatbularis ──────────────────────
// Sin panel no hay pantalla de Usuarios: el admin invita desde el asistente.
// Son las mismas rutas /usuarios del panel; acá solo se usan dos perfiles.

export type Integrante = {
  id: string; email: string; nombre: string; activo: boolean; creado: string;
  rol: string;
  /** Invitado que todavía no crea su contraseña. */
  pendiente?: boolean;
};
export type Invitacion = { enviada: boolean; enlace: string };

export const equipo = () => api<Integrante[]>('/usuarios');
export const invitar = (d: { nombre: string; email: string; rol: 'ADMIN' | 'ABOGADA' }) =>
  api<Integrante & { invitacion?: Invitacion }>('/usuarios', { method: 'POST', body: d });
export const reenviarInvitacion = (id: string) =>
  api<Invitacion>(`/usuarios/${id}/invitacion`, { method: 'POST' });
export const editarIntegrante = (id: string, d: { activo?: boolean; rol?: 'ADMIN' | 'ABOGADA' }) =>
  api<Integrante>(`/usuarios/${id}`, { method: 'PATCH', body: d });

/** ¿El error dice que el saldo no alcanza? La pantalla lo usa para ofrecer
 *  cargar saldo en vez de mostrar un error genérico. */
export function sinSaldo(e: unknown): boolean {
  const err = e as ApiError;
  return err instanceof ApiError && err.status === 429 && err.datos?.motivo === 'saldo_ia';
}

export const listarConversaciones = () =>
  api<ConversacionResumen[]>('/asistente/conversaciones');

export const obtenerConversacion = (id: string) =>
  api<Conversacion>(`/asistente/conversaciones/${id}`);

export const eliminarConversacion = (id: string) =>
  api(`/asistente/conversaciones/${id}`, { method: 'DELETE' });

/** Una consulta puede tardar minutos: el modelo busca en sitios del Poder
 *  Judicial y puede redactar un escrito completo de decenas de miles de
 *  tokens. Sin timeout propio, el navegador cortaría antes de tiempo. */
/** Lo que va pasando mientras el modelo trabaja. */
export type EnVivo = {
  /** Está pensando y todavía no escribe. Con razonamiento adaptativo esa pausa
   *  llega al medio minuto: es el silencio que parece un cuelgue. */
  onPensando?: (segundos: number) => void;
  /** Un pedazo más del escrito. */
  onTexto?: (pedazo: string) => void;
};

const MSJ_CORTE = 'Se cortó la conexión mientras se escribía la respuesta.';

/** ¿La consulta se quedó sin conexión a mitad de camino? En ese caso puede
 *  seguir corriendo en el servidor, y hay que esperarla en vez de reenviarla. */
export const seCortoLaConexion = (e: unknown) => e instanceof ApiError && e.status === 0;

/** ¿El servidor rechazó la pregunta porque ya hay otra respondiéndose en la
 *  misma conversación? No se cobró nada; la respuesta que vale es la otra. */
export const hayConsultaEnCurso = (e: unknown) =>
  e instanceof ApiError && e.status === 409 && e.datos?.motivo === 'consulta_en_curso';

/** Hace la consulta y va entregando el escrito a medida que se escribe.
 *
 *  Reemplazó a una petición muda de cuatro minutos, que tenía dos problemas: el
 *  abogado no sabía si seguía viva, y cualquier corte la perdía entera —con el
 *  escrito ya guardado y cobrado del otro lado—. Un flujo que manda datos cada
 *  pocos segundos no lo corta nadie.
 *
 *  El evento final trae el texto COMPLETO otra vez. No es redundante: es lo que
 *  garantiza que lo que quedó en pantalla sea exactamente lo que se guardó. */
export async function consultar(
  pregunta: string,
  conversacionId?: string,
  razonamiento?: Razonamiento,
  enVivo: EnVivo = {},
  /** Qué caso confirmó el abogado para esta pregunta, o que no es de ningún
   *  caso (sinCaso). Ver pideConfirmarCaso. */
  caso: { casoId?: string; sinCaso?: boolean } = {},
  /** Lo que manda el complemento de Word: el texto seleccionado y desde dónde
   *  se preguntó. */
  word: { seleccion?: string; documento?: string; desde?: 'word' } = {},
): Promise<RespuestaConsulta> {
  const cabeceras: Record<string, string> = { 'Content-Type': 'application/json' };
  const t = getToken();
  if (t) cabeceras['Authorization'] = `Bearer ${t}`;

  let res: Response;
  try {
    res = await fetch(`${API_URL}/asistente/consulta`, {
      method: 'POST',
      headers: cabeceras,
      body: JSON.stringify({ pregunta, conversacionId, razonamiento, ...caso, ...word }),
    });
  } catch {
    // Puede haber llegado al servidor y estar corriendo: la pantalla va a
    // buscarla antes de darla por perdida (ver seCortoLaConexion).
    throw new ApiError(0, MSJ_CORTE);
  }

  // Los controles que fallan antes de abrir el flujo —sin saldo, sin permiso—
  // siguen llegando como JSON con su código HTTP. Solo cuando pasan todos se
  // cambia a flujo de eventos.
  if (!res.ok || !res.headers.get('content-type')?.includes('text/event-stream')) {
    let datos: any = {};
    try { datos = await res.json(); } catch { /* cuerpo vacío o no-JSON */ }
    // `datos` se adjunta después de construir, igual que en api(): es lo que
    // permite distinguir el 429 por saldo agotado de un error cualquiera.
    const err = new ApiError(res.status, datos?.error || 'El asistente no pudo responder.');
    err.datos = datos;
    throw err;
  }

  const lector = res.body!.getReader();
  const decodificador = new TextDecoder();
  let pendiente = '';
  let final: RespuestaConsulta | null = null;
  let fallo: string | null = null;

  for (;;) {
    let trozo: ReadableStreamReadResult<Uint8Array>;
    // Un teléfono corta el flujo al bloquear la pantalla o cambiar de
    // aplicación. Safari lo informa como "Load failed", que no dice nada.
    try { trozo = await lector.read(); } catch { throw new ApiError(0, MSJ_CORTE); }
    const { done, value } = trozo;
    if (done) break;
    pendiente += decodificador.decode(value, { stream: true });

    // Los eventos van separados por una línea en blanco. Se procesa lo que esté
    // completo y se guarda el resto: un evento puede llegar partido en dos
    // pedazos de red.
    let corte: number;
    while ((corte = pendiente.indexOf('\n\n')) !== -1) {
      const bruto = pendiente.slice(0, corte);
      pendiente = pendiente.slice(corte + 2);

      const evento = bruto.match(/^event: (.+)$/m)?.[1];
      const datos = bruto.match(/^data: (.+)$/m)?.[1];
      if (!evento || !datos) continue;
      const d = JSON.parse(datos);

      if (evento === 'delta') enVivo.onTexto?.(d.t);
      else if (evento === 'pensando') enVivo.onPensando?.(d.segundos ?? 0);
      else if (evento === 'fin') final = d;
      else if (evento === 'error') fallo = d.error;
    }
  }

  if (fallo) throw new ApiError(502, fallo);
  // El flujo se cortó sin cierre: el escrito puede estar guardado igual, y de
  // eso se encarga el rescate de la pantalla.
  if (!final) throw new ApiError(0, MSJ_CORTE);
  return final;
}

/** Abre una conversación vacía. Sirve para adjuntar antes de escribir nada:
 *  el archivo necesita una consulta donde colgarse. */
export const crearConversacion = () =>
  api<Conversacion>('/asistente/conversaciones', { method: 'POST', body: {} });

/** Adjunta un archivo a la conversación: el servidor extrae el texto y lo
 *  guarda. El archivo en sí no se almacena — es material de trabajo, no parte
 *  del corpus.
 *
 *  El plazo es amplio porque una imagen o un PDF escaneado no se leen: se
 *  transcriben con el modelo, y eso puede tomar un par de minutos en un
 *  expediente de muchas páginas. */
export async function adjuntarDocumento(conversacionId: string, file: File) {
  const contenidoBase64 = await aBase64(file);
  return api<DocumentoAdjunto>(`/asistente/conversaciones/${conversacionId}/documentos`, {
    method: 'POST',
    body: { nombre: file.name, contenidoBase64, mimeType: file.type },
    timeoutMs: 5 * 60 * 1000,
  });
}

/** Volver a leer un adjunto del caso que falló o quedó interrumpido. */
export const reintentarDocumento = (conversacionId: string, documentoId: string) =>
  api<DocumentoAdjunto>(`/asistente/conversaciones/${conversacionId}/documentos/${documentoId}/reintentar`, { method: 'POST' });

export const quitarDocumento = (conversacionId: string, documentoId: string) =>
  api(`/asistente/conversaciones/${conversacionId}/documentos/${documentoId}`, { method: 'DELETE' });

/* ── Corpus jurídico (SOLO consola dueño) ──────────────────────────────
   La base jurídica es de Tabularis y la comparten todos los estudios, así que
   la administra únicamente el dueño de la plataforma. Estas llamadas van con
   el token de /plataforma, no con el del estudio: por eso todas reciben
   `token` en vez de tomarlo de la sesión del panel. */

export type DocumentoCorpus = {
  id: string;
  nombre: string;
  /** Carpeta en S3, no una materia: la búsqueda mira todo el corpus. */
  categoria: string;
  estado: 'pendiente' | 'procesando' | 'listo' | 'error';
  error: string | null;
  fragmentos: number;
  size: number | null;
  creado: string;
  indexado: string | null;
};

export type FiltroCorpus = 'todos' | 'error' | 'enCurso';
export type ListaCorpus = {
  /** Como mucho 500, los más recientes que cumplen el filtro. */
  documentos: DocumentoCorpus[];
  /** De toda la base, no solo de los que vinieron en la lista. */
  totalDocumentos: number;
  porEstado: Record<DocumentoCorpus['estado'], number>;
  totalFragmentos: number;
  habilitado: boolean;
};
export const LIMITE_LISTA_CORPUS = 500;

export const listarCorpus = (token: string, filtro: { q?: string; estado?: FiltroCorpus } = {}) => {
  const p = new URLSearchParams();
  if (filtro.q?.trim()) p.set('q', filtro.q.trim());
  if (filtro.estado && filtro.estado !== 'todos') p.set('estado', filtro.estado);
  const qs = p.toString();
  return api<ListaCorpus>(`/asistente/corpus${qs ? `?${qs}` : ''}`, { token });
};

/** SHA-256 del archivo en hex, igual que huellaDe en el servidor. Con ella el
 *  servidor reconoce un archivo que ya está en la base aunque venga con otro
 *  nombre, antes de subirlo. */
async function huellaDe(archivo: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await bytesDe(archivo));
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/** La subida directa a S3 no llegó: el navegador no pudo conectar con el
 *  bucket. Si pasa con un archivo pasa con todos (sin red, o el bucket sin
 *  CORS para tabularis.cl), y la consola corta la tanda en vez de recorrerla. */
export class ErrorSubidaS3 extends Error {}

/** El techo general de peticiones por IP del servidor (429 sin motivo; el de
 *  saldo de IA trae motivo y no es esto). Se espera y se reintenta. */
export const esLimiteDePeticiones = (e: unknown) =>
  e instanceof ApiError && e.status === 429 && !e.datos?.motivo;

/** Sube al corpus en tres pasos, igual que los documentos de un caso: se pide
 *  el enlace firmado, el navegador sube directo a S3 y recién entonces se
 *  dispara la indexación. Si el archivo ya está en la base, el primer paso
 *  responde 409 y no se sube nada.
 *
 *  Recibe una Fuente y no un File porque puede venir de dentro de un .zip: se
 *  lee recién acá, de a un archivo, sin descomprimir el .zip entero. */
export async function subirAlCorpus(token: string, fuente: Fuente) {
  const archivo = await fuente.leer();
  const contentType = archivo.type || 'application/octet-stream';
  const r = await api<{ documento: DocumentoCorpus; uploadUrl: string }>('/asistente/corpus', {
    method: 'POST',
    body: { nombre: fuente.name, contentType, size: fuente.size, hash: await huellaDe(archivo) },
    token,
  });

  let put: Response;
  try {
    put = await fetch(r.uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: archivo });
  } catch {
    // fetch no dice por qué ("Failed to fetch", "Load failed"): sin red o un
    // bloqueo de CORS se ven igual desde acá.
    throw new ErrorSubidaS3('no se pudo conectar con el almacenamiento para subir el archivo');
  }
  if (!put.ok) throw new Error(`No se pudo subir el archivo a destino (${put.status}).`);

  await api(`/asistente/corpus/${r.documento.id}/indexar`, { method: 'POST', token });
  return r.documento;
}

export const reindexarCorpus = (token: string, id: string) =>
  api(`/asistente/corpus/${id}/indexar`, { method: 'POST', token });

export const eliminarDelCorpus = (token: string, id: string) =>
  api(`/asistente/corpus/${id}`, { method: 'DELETE', token });

function aBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onload = () => resolve(String(lector.result).split(',')[1] ?? '');
    lector.onerror = () => reject(new Error('No se pudo leer el archivo'));
    lector.readAsDataURL(file);
  });
}
